import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Film, Sparkles, Calendar, Trash2, Copy, RefreshCw, Send, AlertCircle, ChevronLeft, ChevronRight, List, CalendarDays, CheckCircle2, Loader2, AlertTriangle, Volume2, PlayCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  generating: { txt: "Generando…", color: "#A69076", bg: "#F4F0EB" },
  ready: { txt: "Listo", color: "#3a5a44", bg: "#E6F0E8" },
  scheduled: { txt: "Programado", color: "#1A1C1A", bg: "#D9CDBC" },
  publishing: { txt: "Publicando…", color: "#fff", bg: "#A69076" },
  published: { txt: "Publicado", color: "#fff", bg: "#1A1C1A" },
  publish_failed: { txt: "Error al publicar", color: "#7a3a3a", bg: "#F5E0E0" },
  failed: { txt: "Falló", color: "#7a3a3a", bg: "#F5E0E0" },
};

// Expected total seconds Sora 2 takes per duration (used for time-based progress bar)
const EXPECTED_SECS = { 4: 110, 8: 170, 12: 240 };

function computeProgress(reel) {
  if (reel.status !== "generating") return 100;
  const started = new Date(reel.created_at || Date.now()).getTime();
  const elapsed = (Date.now() - started) / 1000;
  const total = EXPECTED_SECS[reel.duration] || 150;
  // Smooth easing: never reaches 100 while generating
  const raw = Math.min(95, (elapsed / total) * 100);
  return Math.max(2, Math.floor(raw));
}

export default function ReelsPanel() {
  const [form, setForm] = useState({ topic: "", service: "general", duration: 4, casting: "auto", music_category: "auto", voiceover: false, voiceover_voice_id: "voice_d", voiceover_text: "" });
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scheduleFor, setScheduleFor] = useState({});
  const [view, setView] = useState("list"); // 'list' | 'calendar'
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState(null);
  const [calendarItems, setCalendarItems] = useState([]);
  const [metaStatus, setMetaStatus] = useState({ configured: false });
  const [tick, setTick] = useState(0); // forces progress re-render every 2s
  const [options, setOptions] = useState({ castings: [], music_categories: [], voices: [] });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewAudio, setPreviewAudio] = useState(null);

  // tick every 2s to animate progress bars
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 2000);
    return () => clearInterval(t);
  }, []);

  const loadMeta = async () => {
    try { const r = await api.get("/meta/status"); setMetaStatus(r.data); } catch (_) { /* noop */ }
  };
  const loadOptions = async () => {
    try {
      const [r, v] = await Promise.all([
        api.get("/reels/options"),
        api.get("/voice/voices").catch(() => ({ data: { voices: [] } })),
      ]);
      setOptions({ ...r.data, voices: v.data?.voices || [] });
    } catch (_) { /* noop */ }
  };
  useEffect(() => { loadMeta(); loadOptions(); }, []);

  const loadCalendar = async () => {
    try { const r = await api.get("/calendar/all"); setCalendarItems(r.data); } catch (_) { /* noop */ }
  };
  useEffect(() => { loadCalendar(); const t = setInterval(loadCalendar, 12000); return () => clearInterval(t); }, []);

  const byDay = useMemo(() => {
    const map = {};
    for (const it of calendarItems) {
      const key = new Date(it.scheduled_for).toISOString().slice(0, 10);
      (map[key] = map[key] || []).push(it);
    }
    return map;
  }, [calendarItems]);

  const rescheduleItem = async (item, newDayKey) => {
    const oldDate = new Date(item.scheduled_for);
    const [y, m, d] = newDayKey.split("-").map(Number);
    const newDate = new Date(y, m - 1, d, oldDate.getHours(), oldDate.getMinutes());
    const iso = newDate.toISOString();
    const endpoint = item.kind === "reel" ? `/reels/${item.id}/schedule` :
                     item.kind === "social" ? `/social-posts/${item.id}/schedule` :
                     `/offers/${item.id}/schedule`;
    try {
      const body = item.kind === "reel" ? { scheduled_for: iso, platform: "both" } : { scheduled_for: iso };
      await api.patch(endpoint, body);
      toast.success("Reprogramado");
      loadCalendar();
    } catch {
      toast.error("Error al reprogramar");
    }
  };

  const load = async () => {
    try { const r = await api.get("/reels"); setReels(r.data); } catch (_) { /* noop */ }
  };
  useEffect(() => {
    load();
    const poll = setInterval(load, 8000);
    return () => clearInterval(poll);
  }, []);

  const generate = async () => {
    if (!form.topic.trim()) { toast.error("Escribe un tema"); return; }
    setLoading(true);
    try {
      const payload = {
        topic: form.topic,
        service: form.service,
        duration: form.duration,
        casting: form.casting === "auto" ? null : form.casting,
        music_category: form.music_category,
        voiceover: form.voiceover,
        voiceover_voice_id: form.voiceover ? form.voiceover_voice_id : null,
        voiceover_text: form.voiceover && form.voiceover_text.trim() ? form.voiceover_text.trim() : null,
      };
      await api.post("/reels/generate", payload);
      toast.success("Reel en generación. Sora tarda 2–5 min.");
      setForm({ ...form, topic: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error iniciando generación");
    } finally { setLoading(false); }
  };

  const schedule = async (rid) => {
    const dt = scheduleFor[rid];
    if (!dt) { toast.error("Elige fecha y hora"); return; }
    try {
      await api.patch(`/reels/${rid}/schedule`, { scheduled_for: new Date(dt).toISOString(), platform: "both" });
      toast.success("Programado. Se publicará automáticamente.");
      load(); loadCalendar();
    } catch { toast.error("Error al programar"); }
  };

  const remove = async (rid) => {
    if (!confirm("¿Eliminar este reel?")) return;
    await api.delete(`/reels/${rid}`);
    setReels(reels.filter((r) => r.id !== rid));
  };

  const copyCaption = (r) => {
    const txt = `${r.caption || ""}\n\n${(r.hashtags || []).map(h => "#" + h).join(" ")}`;
    navigator.clipboard.writeText(txt);
    toast.success("Caption copiada");
  };

  const publishNow = async (r) => {
    if (!metaStatus.configured) {
      // Manual fallback
      copyCaption(r);
      window.open("https://www.instagram.com/create/style/", "_blank");
      toast.info("Caption copiada. Sube el video y pega en Instagram.");
      return;
    }
    if (!confirm("¿Publicar ahora en Instagram + Facebook?")) return;
    try {
      toast.info("Publicando en IG + FB…");
      await api.post(`/meta/publish-now/reel/${r.id}`);
      toast.success("Publicado en Instagram y Facebook");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al publicar");
    }
  };

  const downloadVideo = (r) => {
    const a = document.createElement("a");
    a.href = `${BACKEND}${r.video_url}`;
    a.download = `reel_${r.id}.mp4`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const [coverEditor, setCoverEditor] = useState(null); // { rid, hook }
  const [coverBusy, setCoverBusy] = useState(false);
  const openCoverEditor = (r) => setCoverEditor({
    rid: r.id,
    hook: r.cover_hook || r.hook || r.topic || (r.caption || "").split("\n")[0].slice(0, 70) || "",
  });
  const saveCover = async () => {
    if (!coverEditor?.hook?.trim()) { toast.error("Escribe un gancho fuerte"); return; }
    setCoverBusy(true);
    try {
      const res = await api.post(`/reels/${coverEditor.rid}/generate-cover`, { hook: coverEditor.hook.trim() });
      toast.success("Portada generada");
      setReels((prev) => prev.map((x) => x.id === coverEditor.rid
        ? { ...x, cover_url: res.data.cover_url, cover_hook: res.data.hook }
        : x));
      setCoverEditor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar");
    } finally { setCoverBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="reels-panel">
      {/* Meta connection status banner */}
      <MetaStatusBanner status={metaStatus} />

      <div className="grid lg:grid-cols-12 gap-8">
      {/* Generator form */}
      <div className="lg:col-span-5 bg-white rounded-2xl border border-[#E6E2DC] p-7 space-y-5 h-fit">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Reels Studio</div>
          <h3 className="font-display text-2xl text-[#1A1C1A]">Crear un Reel</h3>
          <p className="text-xs text-[#5C5E5C] mt-2 leading-relaxed">Genera video editorial de 4 segundos con Sora 2 + thumbnail + caption con hashtags. Tarda 2–5 minutos.</p>
        </div>
        <div>
          <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Tema</Label>
          <Textarea rows={3} placeholder="Ej. Beneficios del ácido hialurónico en labios" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="reel-topic" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Servicio</Label>
            <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="ortodoncia">Ortodoncia</SelectItem>
                <SelectItem value="estetica">Medicina Estética</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Duración</Label>
            <Select value={String(form.duration)} onValueChange={(v) => setForm({ ...form, duration: Number(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="4">4 segundos</SelectItem>
                <SelectItem value="8">8 segundos</SelectItem>
                <SelectItem value="12">12 segundos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Casting</Label>
            <Select value={form.casting} onValueChange={(v) => setForm({ ...form, casting: v })}>
              <SelectTrigger data-testid="reel-casting"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto (rotación)</SelectItem>
                {options.castings.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-2 block">Música</Label>
            <Select value={form.music_category} onValueChange={(v) => setForm({ ...form, music_category: v })}>
              <SelectTrigger data-testid="reel-music"><SelectValue /></SelectTrigger>
              <SelectContent>
                {options.music_categories.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {form.music_category === "silent" && (
          <div className="text-[10px] text-[#A69076] italic bg-[#F4F0EB] border border-[#E6E2DC] rounded-lg p-2.5 leading-relaxed">
            Modo sin música: descarga el Reel y súbelo desde la app de Instagram para usar audios trending del momento.
          </div>
        )}

        {/* Voiceover · ElevenLabs */}
        <div className="rounded-xl border border-[#E6E2DC] bg-[#FBF8F2] p-3.5 space-y-3" data-testid="reel-voiceover-block">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-[#A69076]" />
              <div>
                <div className="text-[12px] text-[#1A1C1A] font-medium">Voz en off</div>
                <div className="text-[10px] text-[#5C5E5C]">Narración con ElevenLabs sobre la música</div>
              </div>
            </div>
            <Switch checked={form.voiceover} onCheckedChange={(v) => setForm({ ...form, voiceover: v })} data-testid="reel-voiceover-toggle" />
          </div>
          {form.voiceover && (
            <div className="space-y-2.5">
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-1.5 block">Voz</Label>
                <Select value={form.voiceover_voice_id} onValueChange={(v) => setForm({ ...form, voiceover_voice_id: v })}>
                  <SelectTrigger data-testid="reel-voiceover-voice"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(options.voices || []).map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.is_favorite && "⭐ "}{v.label} · <span className="text-[10px] text-[#A69076]">{v.tagline}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C] font-normal mb-1.5 block">Texto del narrador <span className="normal-case text-[9px]">(vacío = se deriva del caption)</span></Label>
                <Textarea rows={2} placeholder="Ej. Tu mejor versión está a una cita. Aparta tu valoración por WhatsApp." value={form.voiceover_text} onChange={(e) => setForm({ ...form, voiceover_text: e.target.value })} data-testid="reel-voiceover-text" />
              </div>
              <Button
                type="button" size="sm" variant="outline"
                disabled={previewLoading}
                onClick={async () => {
                  const text = form.voiceover_text.trim() || `Tu mejor versión está a una cita de distancia. Aparta tu valoración por WhatsApp.`;
                  setPreviewLoading(true); setPreviewAudio(null);
                  try {
                    const r = await api.post("/voice/preview", { text, voice_id: form.voiceover_voice_id });
                    setPreviewAudio(r.data.audio_data_url);
                    toast.success(`Audio listo (${r.data.char_count} chars)`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Error al previsualizar");
                  } finally { setPreviewLoading(false); }
                }}
                data-testid="reel-voiceover-preview"
              >
                {previewLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5 mr-1" />}
                Probar voz
              </Button>
              {previewAudio && (
                <audio controls src={previewAudio} className="w-full h-9 mt-1" data-testid="reel-voiceover-audio" />
              )}
            </div>
          )}
        </div>

        <Button onClick={generate} disabled={loading} className="w-full rounded-full bg-[#1A1C1A] hover:bg-[#A69076] text-white h-11 text-xs uppercase tracking-[0.2em]" data-testid="reel-generate">
          <Sparkles className="w-4 h-4 mr-2" /> {loading ? "Iniciando..." : "Generar reel"}
        </Button>
      </div>

      {/* Reels list / calendar */}
      <div className="lg:col-span-7 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076]">Mis reels ({reels.length})</div>
          <div className="inline-flex rounded-full bg-[#F4F0EB] p-1 border border-[#E6E2DC]">
            <button onClick={() => setView("list")} className={`flex items-center gap-1.5 px-3 h-7 rounded-full text-[10px] uppercase tracking-widest transition-colors ${view === "list" ? "bg-[#1A1C1A] text-white" : "text-[#5C5E5C]"}`} data-testid="reels-view-list">
              <List className="w-3 h-3" /> Lista
            </button>
            <button onClick={() => setView("calendar")} className={`flex items-center gap-1.5 px-3 h-7 rounded-full text-[10px] uppercase tracking-widest transition-colors ${view === "calendar" ? "bg-[#1A1C1A] text-white" : "text-[#5C5E5C]"}`} data-testid="reels-view-calendar">
              <CalendarDays className="w-3 h-3" /> Calendario
            </button>
          </div>
        </div>

        {view === "calendar" && (
          <CalendarView calMonth={calMonth} setCalMonth={setCalMonth} byDay={byDay} selectedDay={selectedDay} setSelectedDay={setSelectedDay} onReschedule={rescheduleItem} />
        )}

        {view === "calendar" && selectedDay && (
          <div className="bg-white rounded-2xl border border-[#A69076] p-5">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-2">Programado para {new Date(selectedDay).toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
            {(byDay[selectedDay] || []).length === 0 ? (
              <p className="text-sm text-[#5C5E5C]">Sin publicaciones programadas para este día.</p>
            ) : (
              <ul className="space-y-2">
                {byDay[selectedDay].map((it) => {
                  const kindMeta = it.kind === "reel" ? { label: "REEL", color: "#A69076" } :
                                   it.kind === "social" ? { label: "POST", color: "#7A9180" } :
                                   { label: "OFERTA", color: "#C99776" };
                  return (
                    <li key={`${it.kind}-${it.id}`} className="flex items-center justify-between gap-3 py-2 border-b border-[#E6E2DC] last:border-0">
                      <div className="flex items-center gap-3 min-w-0">
                        {it.thumb_url ? <img src={`${BACKEND}${it.thumb_url}`} className="w-12 h-12 object-cover rounded" alt="" /> : (
                          <div className="w-12 h-12 rounded flex items-center justify-center" style={{ background: kindMeta.color + "22" }}>
                            <Film className="w-5 h-5" style={{ color: kindMeta.color }} />
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[9px] tracking-widest px-1.5 py-0.5 rounded text-white" style={{ background: kindMeta.color }}>{kindMeta.label}</span>
                            <span className="text-sm font-medium text-[#1A1C1A] truncate">{it.title}</span>
                          </div>
                          <div className="text-xs text-[#5C5E5C]">{new Date(it.scheduled_for).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {view === "list" && reels.length === 0 && (
          <div className="bg-white rounded-2xl border border-[#E6E2DC] p-12 text-center" data-testid="reels-empty">
            <Film className="w-10 h-10 text-[#A69076] mx-auto mb-3" />
            <p className="text-sm text-[#5C5E5C]">Aún no has creado reels. Empieza con un tema arriba.</p>
          </div>
        )}
        {view === "list" && reels.map((r) => {
          const st = STATUS_LABEL[r.status] || STATUS_LABEL.generating;
          const progress = r.status === "generating" ? computeProgress(r) : 100;
          return (
            <div key={r.id} className="bg-white rounded-2xl border border-[#E6E2DC] overflow-hidden relative" data-testid={`reel-${r.id}`}>
              <div className="grid sm:grid-cols-[180px_1fr] gap-0">
                <div className="aspect-[9/16] sm:aspect-auto bg-[#1A1C1A] relative">
                  {r.video_url ? (
                    <video src={`${BACKEND}${r.video_url}`} poster={r.cover_url ? `${BACKEND}${r.cover_url}` : (r.thumb_url ? `${BACKEND}${r.thumb_url}` : undefined)}
                           className="w-full h-full object-cover" controls preload="metadata" />
                  ) : r.thumb_url ? (
                    <img src={`${BACKEND}${r.thumb_url}`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {r.status === "generating" ? <Loader2 className="w-6 h-6 text-[#A69076] animate-spin" /> : <Film className="w-6 h-6 text-[#5C5E5C]" />}
                    </div>
                  )}
                  {r.cover_url && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#A69076] text-white text-[9px] tracking-[0.2em] uppercase font-medium">
                      Cover listo
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076]">{r.service} · {r.duration}s</div>
                      <div className="font-medium text-sm text-[#1A1C1A] mt-1">{r.topic}</div>
                    </div>
                    <span className="text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full" style={{ background: st.bg, color: st.color }}>{st.txt}</span>
                  </div>

                  {/* Progress bar for generating state */}
                  {r.status === "generating" && (
                    <div className="mb-4" data-testid={`reel-progress-${r.id}`}>
                      <div className="flex items-center justify-between mb-1.5 text-[10px] tracking-[0.2em] uppercase text-[#5C5E5C]">
                        <span className="flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin text-[#A69076]" /> Sora 2 generando…</span>
                        <span className="tabular-nums text-[#A69076] font-medium">{progress}%</span>
                      </div>
                      <div className="h-1.5 bg-[#F4F0EB] rounded-full overflow-hidden">
                        <div
                          className="h-full transition-all duration-1000 ease-out rounded-full"
                          style={{ width: `${progress}%`, background: "linear-gradient(90deg, #A69076 0%, #C99776 100%)" }}
                        />
                      </div>
                      <div className="text-[10px] text-[#5C5E5C] mt-2 italic">
                        {progress < 35 ? "Generando caption con Claude + thumbnail con Banana Pro…" :
                         progress < 75 ? "Sora 2 está creando el video. Esto tarda 2–5 min." :
                         "Casi listo. Procesando los últimos detalles…"}
                      </div>
                    </div>
                  )}

                  {r.caption && (
                    <p className="text-xs text-[#5C5E5C] line-clamp-3 mb-3 leading-relaxed whitespace-pre-line">{r.caption}</p>
                  )}
                  {r.error && <p className="text-xs text-[#965D5D] mb-3">⚠ {r.error}</p>}
                  {r.last_error && <p className="text-xs text-[#965D5D] mb-3">⚠ Publicación: {r.last_error}</p>}
                  {r.scheduled_for && (
                    <div className="text-xs text-[#1A1C1A] mb-2 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {new Date(r.scheduled_for).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })}</div>
                  )}
                  {(r.status === "ready" || r.status === "scheduled" || r.status === "publish_failed") && (
                    <>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => copyCaption(r)} className="text-xs"><Copy className="w-3 h-3 mr-1" /> Caption</Button>
                        <Button size="sm" variant="outline" onClick={() => downloadVideo(r)} className="text-xs">⬇ Video</Button>
                        <Button size="sm" variant="outline" onClick={() => openCoverEditor(r)} className="text-xs" data-testid={`reel-cover-${r.id}`}>
                          <Sparkles className="w-3 h-3 mr-1" /> {r.cover_url ? "Editar portada" : "Portada"}
                        </Button>
                        <Button size="sm" onClick={() => publishNow(r)} className="bg-[#A69076] hover:bg-[#1A1C1A] text-white text-xs" data-testid={`reel-publish-${r.id}`}>
                          <Send className="w-3 h-3 mr-1" /> {metaStatus.configured ? "Publicar IG + FB" : "Publicar"}
                        </Button>
                      </div>
                      <div className="mt-3 flex gap-2 items-center">
                        <Input type="datetime-local" min={new Date().toISOString().slice(0,16)} className="h-8 text-xs" value={scheduleFor[r.id] || ""} onChange={(e) => setScheduleFor({ ...scheduleFor, [r.id]: e.target.value })} />
                        <Button size="sm" variant="outline" onClick={() => schedule(r.id)} className="text-xs" data-testid={`reel-schedule-${r.id}`}><Calendar className="w-3 h-3 mr-1" /> Programar</Button>
                      </div>
                    </>
                  )}
                  {r.status === "published" && r.meta_result && (
                    <div className="text-xs text-[#3a5a44] flex items-center gap-1.5 mt-2">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Publicado en {r.meta_result.instagram_id ? "Instagram" : ""}{r.meta_result.facebook_id ? " + Facebook" : ""}
                    </div>
                  )}
                  <button onClick={() => remove(r.id)} className="absolute top-3 right-3 p-1.5 text-[#965D5D] hover:bg-[#F5E0E0] rounded">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      </div>

      {/* Cover editor modal */}
      {coverEditor && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="cover-editor-modal" onClick={() => !coverBusy && setCoverEditor(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#A69076] mb-1">Portada del Reel</div>
            <h3 className="font-display text-xl text-[#1A1C1A] mb-1">Escribe un gancho que pare el scroll</h3>
            <p className="text-xs text-[#5C5E5C] mb-4 leading-relaxed">Este texto aparecerá como thumbnail en tu grid de Instagram. Máximo ~85 caracteres.</p>
            <Textarea
              value={coverEditor.hook}
              onChange={(e) => setCoverEditor({ ...coverEditor, hook: e.target.value.slice(0, 120) })}
              placeholder="Ej: El secreto detrás de una sonrisa que enamora"
              rows={3}
              className="text-sm mb-4"
              data-testid="cover-hook-input"
            />
            <div className="text-[10px] text-[#5C5E5C] text-right mb-4">{coverEditor.hook.length}/120</div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setCoverEditor(null)} disabled={coverBusy} className="text-xs">Cancelar</Button>
              <Button size="sm" onClick={saveCover} disabled={coverBusy} className="bg-[#A69076] hover:bg-[#1A1C1A] text-white text-xs" data-testid="cover-save-btn">
                {coverBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                Generar portada
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetaStatusBanner({ status }) {
  if (status.configured && status.token_check?.ok) {
    return (
      <div className="bg-[#E6F0E8] border border-[#7A9180] rounded-xl p-4 flex items-center gap-3" data-testid="meta-status-ok">
        <CheckCircle2 className="w-5 h-5 text-[#3a5a44] shrink-0" />
        <div className="text-xs text-[#1A1C1A]">
          <strong>Meta conectado</strong> · Las publicaciones programadas se subirán automáticamente a Instagram + Facebook ({status.token_check?.page?.name || "Página"}).
        </div>
      </div>
    );
  }
  if (status.configured && !status.token_check?.ok) {
    return (
      <div className="bg-[#F5E0E0] border border-[#965D5D] rounded-xl p-4 flex items-start gap-3" data-testid="meta-status-token-error">
        <AlertTriangle className="w-5 h-5 text-[#965D5D] shrink-0 mt-0.5" />
        <div className="text-xs text-[#1A1C1A]">
          <strong>Token Meta inválido o expirado:</strong> {status.token_check?.error}. Renueva el token en <code>backend/.env</code> (META_PAGE_ACCESS_TOKEN).
        </div>
      </div>
    );
  }
  return (
    <div className="bg-[#F4F0EB] border border-[#A69076] rounded-xl p-4 flex items-start gap-3" data-testid="meta-status-pending">
      <AlertCircle className="w-5 h-5 text-[#A69076] shrink-0 mt-0.5" />
      <div className="text-xs text-[#1A1C1A] leading-relaxed">
        <strong>Auto-publicación pendiente de configurar.</strong> Para que las publicaciones se suban solas a Instagram + Facebook, añade tus credenciales de Meta Graph API en <code>backend/.env</code>:
        <code className="block mt-1.5 text-[10px] bg-[#1A1C1A] text-[#F4F0EB] p-2 rounded">META_PAGE_ID, META_IG_USER_ID, META_PAGE_ACCESS_TOKEN, PUBLIC_BASE_URL</code>
        <span className="block mt-1 italic text-[#5C5E5C]">Mientras tanto, &ldquo;Publicar&rdquo; copia el caption y abre Instagram manualmente.</span>
      </div>
    </div>
  );
}

function CalendarView({ calMonth, setCalMonth, byDay, selectedDay, setSelectedDay, onReschedule }) {
  const [dragOver, setDragOver] = useState(null);
  const year = calMonth.getFullYear();
  const month = calMonth.getMonth();
  const monthName = calMonth.toLocaleDateString("es-MX", { month: "long", year: "numeric" });
  const firstDay = new Date(year, month, 1).getDay();
  const offset = (firstDay + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayKey = new Date().toISOString().slice(0, 10);
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const key = new Date(year, month, d).toISOString().slice(0, 10);
    cells.push({ d, key });
  }

  const nav = (delta) => setCalMonth(new Date(year, month + delta, 1));

  const handleDragStart = (e, item) => {
    e.dataTransfer.setData("application/json", JSON.stringify(item));
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDrop = (e, dayKey) => {
    e.preventDefault();
    setDragOver(null);
    try {
      const item = JSON.parse(e.dataTransfer.getData("application/json"));
      const oldKey = new Date(item.scheduled_for).toISOString().slice(0, 10);
      if (oldKey !== dayKey) onReschedule(item, dayKey);
    } catch (_) { /* noop */ }
  };

  const KIND_COLOR = { reel: "#A69076", social: "#7A9180", offer: "#C99776" };

  return (
    <div className="bg-white rounded-2xl border border-[#E6E2DC] p-6" data-testid="reels-calendar">
      <div className="flex items-center justify-between mb-5">
        <button onClick={() => nav(-1)} className="p-2 hover:bg-[#F4F0EB] rounded-full" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
        <div className="font-display text-xl text-[#1A1C1A] capitalize">{monthName}</div>
        <button onClick={() => nav(1)} className="p-2 hover:bg-[#F4F0EB] rounded-full" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] tracking-widest uppercase text-[#5C5E5C] mb-2">
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={i} />;
          const items = byDay[c.key] || [];
          const isToday = c.key === todayKey;
          const isSelected = c.key === selectedDay;
          const isDragOver = dragOver === c.key;
          return (
            <div
              key={c.key}
              onClick={() => setSelectedDay(c.key)}
              onDragOver={(e) => { e.preventDefault(); setDragOver(c.key); }}
              onDragLeave={() => setDragOver((prev) => prev === c.key ? null : prev)}
              onDrop={(e) => handleDrop(e, c.key)}
              data-testid={`cal-day-${c.d}`}
              className={`relative min-h-[80px] rounded-lg border p-1.5 text-left cursor-pointer transition-all ${
                isDragOver ? "border-[#A69076] bg-[#F4F0EB] ring-2 ring-[#A69076]/50" :
                isSelected ? "border-[#A69076] bg-[#F4F0EB]" :
                isToday ? "border-[#1A1C1A] bg-white" :
                items.length > 0 ? "border-[#D9CDBC] bg-[#FAFAFA]" :
                "border-transparent hover:bg-[#F4F0EB]"
              }`}
            >
              <div className={`text-xs ${isToday ? "font-bold" : ""} text-[#1A1C1A]`}>{c.d}</div>
              <div className="mt-1 space-y-0.5">
                {items.slice(0, 2).map((it) => (
                  <div
                    key={`${it.kind}-${it.id}`}
                    draggable
                    onDragStart={(e) => { e.stopPropagation(); handleDragStart(e, it); }}
                    onClick={(e) => e.stopPropagation()}
                    className="truncate text-[9px] px-1 py-0.5 rounded text-white cursor-grab active:cursor-grabbing"
                    style={{ background: KIND_COLOR[it.kind] }}
                    title={`${it.kind.toUpperCase()}: ${it.title}`}
                  >
                    {new Date(it.scheduled_for).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })} {it.title.slice(0, 14)}
                  </div>
                ))}
                {items.length > 2 && <div className="text-[9px] text-[#A69076]">+{items.length - 2} más</div>}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-5 pt-4 border-t border-[#E6E2DC] flex items-center justify-between text-xs flex-wrap gap-3">
        <div className="flex items-center gap-3 text-[#5C5E5C]">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-[#A69076]" /> Reel</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-[#7A9180]" /> Post</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-[#C99776]" /> Oferta</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm border border-[#1A1C1A]" /> Hoy</span>
        </div>
        <span className="text-[#5C5E5C] italic">Arrastra una publicación para reprogramarla a otro día.</span>
      </div>
    </div>
  );
}
